import isArray from 'lodash/isArray';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import ModalModule from '../modal';
import Utils from '../utils';

const { Modal, ModalButtonActionType } = ModalModule;
const { Logger } = Utils;

/**
 * Parse the input string and split the alphabetic and numeric characters in the string.
 * For example, "1A" will be splitted in "1" and "A".
 * @param {string} str
 * @returns {object}
 */
const splitAlphaNumericString = str => {
	// convert to uppercase
	const upperCaseStr = isString(str) ? str.toUpperCase() : '';

	// check for alphabetic characters in the string
	const matchResult = upperCaseStr.match(/[A-Z]+/);
	const alpha = matchResult ? matchResult[0] : '';
	const matchIndex = matchResult ? matchResult.index : -1;

	// get the numeric characters in the string
	const numericStr = upperCaseStr.replace(alpha, '');

	return {
		// alaphabetic characters in the string
		alpha,

		// numeric characters in the string
		numericStr,

		// covert fractions to decimal equivalents
		// (copy toDecimal method from AddressCleanseManager.js in Ext project)
		numericValue:
			numericStr !== ''
				? parseFloat(
						numericStr
							.replace(/ 1\/2/g, '.5')
							.replace(/1\/2/g, '.5')
							.replace(/ 1\/4/g, '.25')
							.replace(/1\/4/g, '.25')
							.replace(/ 3\/4/g, '.75')
							.replace(/3\/4/g, '.75')
					)
				: null,

		// `true` if the string starts with alphabetic character(s)
		startWithAlpha: matchIndex === 0,
	};
};

/**
 * Build options array based on from and to. Examples:
 * from '6S' to '6W' returns ['6S', '6T', '6U', '6V', '6W']
 * from '101' to '122' returns ['101', '102', '103', '104', ..., '122']
 * from '8401 1/2' to '8403' returns ['8401', '8401 1/2', '8402', '8403']
 * @param {string} from
 * @param {string} to
 * @returns {array}
 */
const buildRangeOptions = (from, to) => {
	const options = [];

	// split aplha/numeric characters in from string
	const fromObject = splitAlphaNumericString(from);

	// split alpha/numeric characters in to string
	const toObject = splitAlphaNumericString(to);

	let begin;
	let end;
	let appendix;

	// if start and end's numeric string don't match, it means the range is in numeric, e.g.,
	// A1, A2, A3, ...
	if (fromObject.numericStr !== toObject.numericStr) {
		// first we need to account for fractions in street number
		// (https://pe.usps.com/text/pub28/28ape_004.htm)

		// if from is a fraction, begin starts with the integer after the fraction
		begin =
			fromObject.numericValue % 1
				? Math.ceil(fromObject.numericValue)
				: fromObject.numericValue;

		// if to is a fraction, end starts the integer before the fraction
		end =
			toObject.numericValue % 1
				? Math.floor(toObject.numericValue)
				: toObject.numericValue;

		// build the range
		for (let i = begin; i <= end; i++) {
			options.push(i);
		}

		// if from is a fraction, add the fraction option to the beginning of the options
		if (fromObject.numericValue % 1) {
			options.unshift(fromObject.numericStr);
		}

		// if end is a fraction, add the fraction option to the end of the options
		if (toObject.numericValue % 1) {
			options.push(toObject.numericStr);
		}

		return options.map(optionStr =>
			fromObject.startWithAlpha
				? `${fromObject.alpha}${optionStr}`
				: `${optionStr}${fromObject.alpha}`
		);
	}

	// else the range is in alphabet, e.g.
	// A, B, C, ...
	else if (fromObject.alpha !== toObject.alpha) {
		// get ASCII code on the last character (if there are more than one characters)
		begin = fromObject.alpha.charCodeAt(fromObject.alpha.length - 1);
		end = toObject.alpha.charCodeAt(toObject.alpha.length - 1);
		appendix = fromObject.alpha.slice(0, fromObject.alpha.length - 1);

		// build the range per begin and end
		for (let i = begin; i <= end; i++) {
			options.push(`${appendix}${String.fromCharCode(i)}`);
		}

		return options.map(optionStr =>
			fromObject.startWithAlpha
				? `${optionStr}${fromObject.numericStr}`
				: `${fromObject.numericStr}${optionStr}`
		);
	}

	return options;
};

// RangeSelect component
const RangeSelect = ({
	name,
	className,
	hasRange,
	rangeStart,
	rangeEnd,
	onRangeChange,
}) => {
	if (hasRange) {
		const options = buildRangeOptions(rangeStart, rangeEnd);

		return (
			<select className={className} name={name} onChange={onRangeChange}>
				{options.map(optionStr => (
					<option key={optionStr} value={optionStr}>
						{optionStr}
					</option>
				))}
			</select>
		);
	}

	return null;
};

class MultiSelectModal extends Component {
	constructor(props) {
		super(props);

		const { data = {} } = props;
		const { candidateAddresses = [] } = data;

		this.state = {
			candidateAddresses: candidateAddresses.map(address => ({
				...address,
				...this.parseAddressRangeOptions(address.addressLine1),
			})),
			selectedRow: null,
			prefixRangeValue: null,
			suffixRangeValue: null,
		};
	}

	/**
	 * Parse address to get prefix and/or suffix range options.
	 * @param {string} address
	 * @return {object}
	 * - {string} addressWithoutRange - address after removing the range options
	 * - {boolean} hasPrefixRange - true if the address contains prefix range options
	 * - {string} prefixRangeStart
	 * - {string} prefixRangeEnd
	 * - {boolean} hasSuffixRange - true if the address contains suffix range options
	 * - {string} suffixRangeStart
	 * - {string} suffixRangeEnd
	 */
	parseAddressRangeOptions = address => {
		/**
		 * Extract the range options from the address.
		 * It's possible that there are prefix and suffix range options existing at the same time.
		 *
		 * Examples:
		 * (1) INPUT: (Range 2901 - 2919) S FLOWER ST APT (Range 84 1/2 - 84) -> OUTPUT: ["(Range 2901 - 2919)", "(Range 84 1/2 - 84)"]
		 * (2) INPUT: "2913 S FLOWER ST APT (Range A1 - A9)" -> OUTPUT: ["(Range A1 - A9)"]
		 */
		const addressRangePattern = /\(Range[\s\w/]*-[\s\w/]*\)/g;
		const matchResult = address.match(addressRangePattern);
		const result = {
			addressWithoutRange: address.replace(addressRangePattern, ''),
			hasPrefixRange: false,
			prefixRangeStart: '',
			prefixRangeEnd: '',
			hasSuffixRange: false,
			suffixRangeStart: '',
			suffixRangeEnd: '',
		};

		if (matchResult) {
			/**
			 * Get the range start and end option value by capturing the following two groups:
			 * (1) group1 : \w+(\s\d\/\d)?(?=\s-\s)
			 * 	- \w+ -> matches any word character (one and unlimited times)
			 * 	- (\s\d\/\d)? -> matches any fraction (0 or 1 times)
			 * 	- (?=\s-\s) -> find the 1st group which has " - " after it
			 * (2) group2 : \w+(\s\d\/\d)?(?=\))
			 * 	- \w+ -> matches any word character (one and unlimited times)
			 * 	- (\s\d\/\d)? -> matches any fraction (0 or 1 times)
			 * 	- (?=\)) -> find the 2nd group which has ")" after it
			 *
			 * Examples:
			 * (1) INPUT: "(Range 11B - 11Z)" -> OUTPUT: ["11B", "11Z"]
			 * (2) INPUT: "(Range 88 1/4 - 89)" -> OUTPUT: ["88 1/4", "89"]
			 * (3) INPUT: "(Range A12 - A50)" -> OUTPUT: ["A12", "A50"]
			 * (4) INPUT: "(Range 2901 - 2999)" -> OUTPUT: ["2901", "2999"]
			 * (5) INPUT: "(Range A - Z)" -> OUTPUT: ["A", "Z"]
			 */
			const rangePattern = /\w+(\s\d\/\d)?(?=\s-\s)|\w+(\s\d\/\d)?(?=\))/g;

			// address only contains either prefix or suffix range options.
			if (matchResult.length === 1) {
				const isPrefixRange = address.indexOf(matchResult[0]) === 0;
				const range = matchResult[0].match(rangePattern);
				const [rangeStart, rangeEnd] = isArray(range) ? range : [];

				if (isPrefixRange) {
					result.hasPrefixRange = true;
					result.prefixRangeStart = rangeStart || '';
					result.prefixRangeEnd = rangeEnd || '';
				} else {
					result.hasSuffixRange = true;
					result.suffixRangeStart = rangeStart || '';
					result.suffixRangeEnd = rangeEnd || '';
				}
			}
			// address contains both prefix range and suffix range options.
			else if (matchResult.length === 2) {
				const prefixRange = matchResult[0].match(rangePattern);
				const suffixRange = matchResult[1].match(rangePattern);

				if (isArray(prefixRange)) {
					result.hasPrefixRange = true;
					result.prefixRangeStart = prefixRange[0] || '';
					result.prefixRangeEnd = prefixRange[1] || '';
				}

				if (isArray(suffixRange)) {
					result.hasSuffixRange = true;
					result.suffixRangeStart = suffixRange[0] || '';
					result.suffixRangeEnd = suffixRange[1] || '';
				}
			}
		}

		return result;
	};

	/**
	 * candidate_addresses array returned from validate request does not contain `name`, `company_name`,
	 * and `country_code` info in the candidate addresses. These missing info could be found in
	 * `matched_address`. Merge the missing information in the selected address and return it.
	 * @returns {object}
	 */
	processSelectedAddress = () => {
		const {
			data: { matchedAddress },
		} = this.props;
		const {
			candidateAddresses,
			selectedRow,
			prefixRangeValue,
			suffixRangeValue,
		} = this.state;
		const addressRow = candidateAddresses[selectedRow];

		const countryCode = isObject(matchedAddress)
			? matchedAddress.countryCode
			: addressRow.countryCode;
		const companyName = isObject(matchedAddress)
			? matchedAddress.companyName
			: addressRow.companyName;
		const name = isObject(matchedAddress)
			? matchedAddress.name
			: addressRow.name;

		let streetAddress = addressRow.addressWithoutRange;

		// user selected a row and left the default value of the range select unchanged
		if (addressRow.hasSuffixRange) {
			streetAddress += !suffixRangeValue
				? addressRow.suffixRangeStart
				: suffixRangeValue;
		}

		// user selected a row and left the default value of the range select unchanged
		if (addressRow.hasPrefixRange) {
			streetAddress =
				(!prefixRangeValue ? addressRow.prefixRangeStart : prefixRangeValue) +
				streetAddress;
		}

		// merge name and company name into the selected address object
		return {
			...addressRow,
			addressLine1: streetAddress,
			name,
			companyName,
			countryCode,
		};
	};

	onRowSelect = e => {
		const { value } = e.target;

		this.setState({
			selectedRow: Number(value),
			prefixRangeValue: null,
			suffixRangeValue: null,
		});
	};

	onRangeChange = e => {
		const { name, value } = e.target;
		const row = name.substring(6);
		const rangeType = name.substring(0, 6);

		this.setState({
			selectedRow: Number(row),
			[`${rangeType}RangeValue`]: value,
		});
	};

	onAcceptButtonClick = () => {
		const {
			modalId,
			data: { callback },
		} = this.props;

		const selectedAddress = this.processSelectedAddress();

		// WEBAPPS-31593, log when user clicks "Accept" button
		Logger.logToServer({
			category: 'multiselect_dialog',
			action: 'click',
			ui_element: 'accept_button',
		});

		callback(modalId, selectedAddress);
	};

	onModalClose = () => {
		const { data, modalId } = this.props;
		const { callback } = data;

		callback(modalId, null);
	};

	render() {
		const { modalId } = this.props;
		const { candidateAddresses, selectedRow } = this.state;
		const primaryButton = {
			type: ModalButtonActionType.Ok,
			text: 'Accept',
			onClick: this.onAcceptButtonClick,
			onClose: this.onModalClose,
			disabled: !isNumber(selectedRow),
			dataqa: 'multi-select-accept-button',
		};

		return (
			<Modal
				modalId={modalId}
				className="common-cleanse-address multi-select-modal"
				primaryButton={primaryButton}
				header="Exact Address Not Found"
			>
				<div className="multi-select-modal-content">
					<h3>Your address requires more details:</h3>
					<p>
						The address standardization system could not find your exact
						address.
					</p>
					<p>Select an address from the list below that matches it:</p>
					<table border="1">
						<thead>
							<tr>
								<th>&nbsp;</th>
								<th>Street</th>
								<th>City</th>
								<th>State</th>
								<th>ZIP</th>
							</tr>
						</thead>
						<tbody>
							{candidateAddresses.map((item, index) => (
								<tr key={index}>
									<td>
										<input
											type="radio"
											name="addressSelection"
											id={`item${index}`}
											value={index}
											checked={selectedRow !== null && selectedRow === index}
											onChange={this.onRowSelect}
										/>
									</td>
									<td>
										<RangeSelect
											className="prefix-select"
											name={`prefix${index}`}
											hasRange={item.hasPrefixRange}
											rangeStart={item.prefixRangeStart}
											rangeEnd={item.prefixRangeEnd}
											onRangeChange={this.onRangeChange}
										/>

										{item.addressWithoutRange}
										<RangeSelect
											className="suffix-select"
											name={`suffix${index}`}
											hasRange={item.hasSuffixRange}
											rangeStart={item.suffixRangeStart}
											rangeEnd={item.suffixRangeEnd}
											onRangeChange={this.onRangeChange}
										/>
									</td>
									<td>{item.city}</td>
									<td>{item.stateProvince}</td>
									<td>{item.postalCode}</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			</Modal>
		);
	}
}

MultiSelectModal.propType = {
	data: PropTypes.shape({
		// a list of suggested candidate addresses.
		candidateAddresses: PropTypes.array.isRequired,

		// pass "modalId" and "selectedAddress" back to the caller.
		callback: PropTypes.func,
	}),
};

export default MultiSelectModal;
